import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, RSD } from "../../lib/api";
import TechAnalytics from "./TechAnalytics";

const PRESETS = [["today", "Danas"], ["yesterday", "Juče"], ["last_7", "7 dana"], ["last_28", "28 dana"],
                 ["this_month", "Ovaj mesec"], ["previous_month", "Prošli mesec"], ["custom", "Prilagođeno"]];
const TABS = [["pregled", "Pregled"], ["saobracaj", "Saobraćaj"], ["funnel", "Funnel"],
              ["porudzbine", "Porudžbine"], ["retencija", "Retencija"], ["promocije", "Promocije"],
              ["b2b", "B2B"], ["tehnicki", "Tehnički"], ["pretraga", "Pretraga"], ["kampanje", "Kampanje"], ["uzivo", "Uživo"]];
const DEVICES = [["", "Svi uređaji"], ["mobile", "Mobilni"], ["desktop", "Desktop"], ["tablet", "Tablet"], ["pwa", "PWA"]];
const SOURCES = [["all", "Sve porudžbine"], ["ponesi", "Ponesi.rs"], ["vista", "Vista"]];
const CUSTOMERS = [["", "Svi kupci"], ["new", "Novi kupci"], ["returning", "Vraćeni kupci"]];
const REALTIME_MS = 15000;

const pct = (v) => (v === null || v === undefined ? "—" : `${(v * 100).toFixed(1)}%`);
const dt = (s) => (s ? new Date(s).toLocaleDateString("sr-RS") : "—");

function Delta({ c }) {
  if (!c || c.percent === undefined) return null;
  if (c.state === "new") return <span className="text-[11px] font-black text-brand-green">Novo</span>;
  if (c.percent === null) return <span className="text-[11px] text-slate-400">—</span>;
  const up = c.percent > 0;
  return (
    <span className={`text-[11px] font-black ${up ? "text-brand-green" : c.percent < 0 ? "text-brand-red" : "text-slate-400"}`}>
      {up ? "+" : ""}{c.percent}%
    </span>
  );
}

function Kpi({ label, c, fmt = (v) => v, hint, testid }) {
  return (
    <div className="bg-white rounded-2xl border p-3" data-testid={testid} title={hint}>
      <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">{label}</p>
      <p className="font-display font-black text-xl mt-0.5">{fmt(c?.current)}</p>
      <Delta c={c} />
    </div>
  );
}

function Bars({ rows, labelKey, valueKey, testid }) {
  rows = Array.isArray(rows) ? rows : [];
  const max = Math.max(1, ...rows.map((r) => r[valueKey] || 0));
  if (!rows.length) return <p className="bg-white rounded-2xl border p-3 text-sm text-slate-400">Nema podataka za izabrani period.</p>;
  return (
    <div className="bg-white rounded-2xl border divide-y" data-testid={testid}>
      {rows.map((r) => (
        <div key={String(r[labelKey])} className="p-2.5">
          <div className="flex items-baseline justify-between text-sm">
            <span className="truncate font-semibold">{r[labelKey]}</span>
            <span className="text-slate-500 shrink-0">{r[valueKey]}</span>
          </div>
          <div className="h-1.5 mt-1 rounded bg-slate-100">
            <div className="h-1.5 rounded bg-brand-red" style={{ width: `${((r[valueKey] || 0) / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Table({ cols, rows, testid }) {
  rows = Array.isArray(rows) ? rows : [];
  if (!rows.length) return <p data-testid={testid} className="bg-white rounded-2xl border p-3 text-sm text-slate-400">Nema podataka za izabrani period.</p>;
  return (
    <div className="bg-white rounded-2xl border overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm min-w-[520px]">
        <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-400">
          {cols.map((c) => <th key={c.key} className="p-2.5 font-black">{c.label}</th>)}
        </tr></thead>
        <tbody className="divide-y">
          {rows.map((r, i) => (
            <tr key={i}>{cols.map((c) => <td key={c.key} className="p-2.5">{c.fmt ? c.fmt(r[c.key], r) : (r[c.key] ?? "—")}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PlatformAnalytics() {
  const [sp, setSp] = useSearchParams();
  // Inner tab uses its own query key ("pa") so it cannot collide with the admin panel's section param.
  const tab = sp.get("pa") || "pregled";
  const f = {
    preset: sp.get("preset") || "last_7", date_from: sp.get("date_from") || "", date_to: sp.get("date_to") || "",
    city_id: sp.get("city_id") || "", restaurant_id: sp.get("restaurant_id") || "",
    order_source: sp.get("order_source") || "all", traffic_source: sp.get("traffic_source") || "",
    campaign: sp.get("campaign") || "", device: sp.get("device") || "", customer_type: sp.get("customer_type") || "",
  };
  const setF = (patch) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setSp(next, { replace: true });
  };
  const [cities, setCities] = useState([]);
  const [rests, setRests] = useState([]);
  const [data, setData] = useState(null);
  const [live, setLive] = useState(null);
  const [liveAt, setLiveAt] = useState(null);
  const [state, setState] = useState("loading");
  const [stale, setStale] = useState(false);
  const techMode = sp.get("tw") || "range";

  useEffect(() => {
    api.get("/cities").then(({ data }) => setCities(data || [])).catch(() => {});
    api.get("/admin/restaurants").then(({ data }) => setRests(data)).catch(() => {});
  }, []);

  const qs = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    if (f.preset === "custom" && (!f.date_from || !f.date_to)) return null;
    return p.toString();
  }, [sp]); // eslint-disable-line react-hooks/exhaustive-deps

  // Historical sections load on demand (per tab + filter change) — never on the realtime interval.
  useEffect(() => {
    if (tab === "uzivo") { setState("ready"); return; }
    const query = qs();
    if (query === null) { setState("invalid"); return; }
    const path = { pregled: "overview", saobracaj: "traffic", funnel: "funnel",
                   porudzbine: "orders", retencija: "retention", kampanje: "campaigns" }[tab] || "overview";
    setState("loading"); setData(null);
    const url = tab === "pretraga"
      ? `/admin/analytics/searches?days=30${f.city_id ? `&city_id=${f.city_id}` : ""}`
      : tab === "promocije"
      ? `/admin/analytics/platform/promotions?${f.city_id ? `city_id=${f.city_id}&` : ""}${f.restaurant_id ? `restaurant_id=${f.restaurant_id}` : ""}`
      : tab === "b2b"
      ? `/admin/analytics/b2b?${query}`
      : tab === "tehnicki"
      ? `/admin/analytics/tech?${query}${techMode !== "range" ? `&window=${techMode}` : ""}`
      : `/admin/analytics/platform/${path}?${query}`;
    let alive = true;
    api.get(url).then(({ data }) => { if (alive) { setData(data); setState("ready"); } })
      .catch(() => { if (alive) setState("error"); });
    return () => { alive = false; };
  }, [tab, sp]); // eslint-disable-line react-hooks/exhaustive-deps

  // Realtime: single lightweight endpoint, 15s, paused when the tab is hidden, no overlap.
  const busy = useRef(false);
  useEffect(() => {
    if (tab !== "uzivo") return;
    let timer = null;
    const tick = async () => {
      if (busy.current || document.visibilityState === "hidden") return;
      busy.current = true;
      try {
        const { data } = await api.get("/admin/analytics/platform/realtime");
        setLive(data); setLiveAt(new Date()); setStale(false);
      } catch { setStale(true); } finally { busy.current = false; }
    };
    tick();
    timer = setInterval(tick, REALTIME_MS);
    return () => clearInterval(timer);
  }, [tab]);

  const exportCsv = (kind) => {
    window.open(`${api.defaults.baseURL}/admin/analytics/platform/export?kind=${kind}&${qs() || ""}`, "_blank");
  };

  return (
    <div data-testid="platform-analytics">
      <div className="flex flex-wrap gap-1.5 mb-2">
        {PRESETS.map(([k, l]) => (
          <button key={k} data-testid={`pa-preset-${k}`} onClick={() => setF({ preset: k })}
            className={`px-3 py-1.5 rounded-full text-xs font-bold ${f.preset === k ? "bg-brand-red text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
        ))}
      </div>
      {f.preset === "custom" && (
        <div className="flex flex-wrap items-end gap-2 mb-2">
          <label className="text-xs font-bold text-slate-500">Od
            <input data-testid="pa-date-from" type="date" value={f.date_from} onChange={(e) => setF({ date_from: e.target.value })}
              className="block mt-1 px-2 py-1.5 rounded-lg border-2 border-slate-200" /></label>
          <label className="text-xs font-bold text-slate-500">Do
            <input data-testid="pa-date-to" type="date" value={f.date_to} onChange={(e) => setF({ date_to: e.target.value })}
              className="block mt-1 px-2 py-1.5 rounded-lg border-2 border-slate-200" /></label>
          {state === "invalid" && <span data-testid="pa-range-invalid" className="text-xs font-bold text-brand-red">Izaberite oba datuma (od ≤ do).</span>}
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-3">
        <select data-testid="pa-city" value={f.city_id} onChange={(e) => setF({ city_id: e.target.value })} className="px-2 py-2 rounded-lg border-2 border-slate-200 text-sm">
          <option value="">Svi gradovi</option>
          {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select data-testid="pa-restaurant" value={f.restaurant_id} onChange={(e) => setF({ restaurant_id: e.target.value })} className="px-2 py-2 rounded-lg border-2 border-slate-200 text-sm">
          <option value="">Svi restorani</option>
          {rests.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <select data-testid="pa-order-source" value={f.order_source} onChange={(e) => setF({ order_source: e.target.value })} className="px-2 py-2 rounded-lg border-2 border-slate-200 text-sm">
          {SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input data-testid="pa-traffic-source" value={f.traffic_source} onChange={(e) => setF({ traffic_source: e.target.value })} placeholder="Izvor saobraćaja" className="px-2 py-2 rounded-lg border-2 border-slate-200 text-sm" />
        <input data-testid="pa-campaign" value={f.campaign} onChange={(e) => setF({ campaign: e.target.value })} placeholder="Kampanja" className="px-2 py-2 rounded-lg border-2 border-slate-200 text-sm" />
        <select data-testid="pa-device" value={f.device} onChange={(e) => setF({ device: e.target.value })} className="px-2 py-2 rounded-lg border-2 border-slate-200 text-sm">
          {DEVICES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select data-testid="pa-customer-type" value={f.customer_type} onChange={(e) => setF({ customer_type: e.target.value })} className="px-2 py-2 rounded-lg border-2 border-slate-200 text-sm">
          {CUSTOMERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      <div className="flex flex-wrap gap-1 mb-3 border-b pb-2">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`pa-tab-${k}`} onClick={() => setF({ pa: k })}
            className={`px-3 py-1.5 rounded-full text-xs font-bold ${tab === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {tab === "uzivo" ? (
        <div className="space-y-2" data-testid="pa-realtime">
          <p className="text-xs text-slate-400">
            Poslednje ažuriranje: {liveAt ? liveAt.toLocaleTimeString("sr-RS") : "—"}
            {stale && <span className="text-brand-red font-bold"> · osvežavanje nije uspelo, prikazani su poslednji podaci</span>}
          </p>
          {!live ? <p className="text-slate-400 text-sm">Učitavanje…</p> : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <Kpi label="Aktivne sesije" c={{ current: live.active_sessions }} testid="pa-live-sessions" />
                <Kpi label="Aktivne korpe" c={{ current: live.active_carts }} testid="pa-live-carts" />
                <Kpi label="Aktivne porudžbine" c={{ current: live.active_orders }} testid="pa-live-orders" />
              </div>
              <Bars rows={live.by_city.map((c) => ({ city: c.city_id, sessions: c.sessions }))} labelKey="city" valueKey="sessions" testid="pa-live-cities" />
              <p className="text-[11px] text-slate-400">Prozor: poslednjih {live.window_minutes} minuta. Bez identiteta kupaca.</p>
            </>
          )}
        </div>
      ) : state === "loading" ? <p className="text-slate-400 text-sm" data-testid="pa-loading">Učitavanje…</p>
        : state === "error" ? <p className="text-brand-red text-sm" data-testid="pa-error">Greška pri učitavanju analitike.</p>
        : state === "invalid" ? null
        : !data ? null : (
        <div className="space-y-4">
          {tab === "pregled" && data.kpis && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                <Kpi label="Posete (sesije)" c={data.kpis.sessions} testid="pa-kpi-sessions" hint="Broj sesija u izabranom periodu." />
                <Kpi label="Unique visitors" c={data.kpis.unique_visitors} testid="pa-kpi-visitors" hint="Jedinstveni anonimni posetioci." />
                <Kpi label="Registracije" c={data.kpis.registrations} testid="pa-kpi-registrations" />
                <Kpi label="Aktivni korisnici" c={data.kpis.active_users} testid="pa-kpi-active-users" hint="Prijavljeni korisnici sa aktivnošću u izabranom periodu." />
                <Kpi label="Završene porudžbine" c={data.kpis.completed_orders} testid="pa-kpi-orders" />
                <Kpi label="Promet" c={data.kpis.completed_revenue} fmt={(v) => RSD(v || 0)} testid="pa-kpi-revenue" />
                <Kpi label="Prosečna korpa" c={data.kpis.avg_order_value} fmt={(v) => RSD(v || 0)} testid="pa-kpi-aov" hint="Prihod od završenih porudžbina / broj završenih porudžbina." />
                <Kpi label="Visit → order" c={data.kpis.visit_to_order_conversion} fmt={pct} testid="pa-kpi-conversion" hint="Procenat praćenih sesija koje su završile porudžbinom." />
                <Kpi label="Returning customer rate" c={data.returning_customer_rate} fmt={pct} testid="pa-kpi-returning" hint="Kupci koji su imali završenu porudžbinu pre perioda i ponovo poručili u periodu." />
              </div>
              <Table testid="pa-customer-mix" cols={[
                { key: "kind", label: "Kupci" }, { key: "customers", label: "Kupaca" },
                { key: "completed_orders", label: "Porudžbine" },
                { key: "revenue", label: "Promet", fmt: (v) => RSD(v) },
                { key: "avg_order_value", label: "AOV", fmt: (v) => RSD(v) }]}
                rows={[{ kind: "Novi", ...data.customer_mix.new }, { kind: "Vraćeni", ...data.customer_mix.returning }]} />
              <p className="text-[11px] text-slate-400">Podaci o ponašanju dostupni od {dt(data.behaviour_available_from)}. Porudžbine i promet postoje i istorijski.</p>
            </>
          )}

          {tab === "saobracaj" && data.series && (
            <>
              <Bars rows={data.series} labelKey="bucket" valueKey="visitors" testid="pa-traffic-series" />
              <div className="grid sm:grid-cols-2 gap-2">
                <Bars rows={data.traffic_source} labelKey="key" valueKey="sessions" testid="pa-traffic-source" />
                <Bars rows={data.device} labelKey="key" valueKey="sessions" testid="pa-traffic-device" />
                <Bars rows={data.language} labelKey="key" valueKey="sessions" testid="pa-traffic-language" />
                <Bars rows={data.city} labelKey="key" valueKey="sessions" testid="pa-traffic-city" />
              </div>
            </>
          )}

          {tab === "funnel" && data.stages && (
            <>
              <div className="bg-white rounded-2xl border divide-y" data-testid="pa-funnel">
                {data.stages.map((s) => (
                  <div key={s.stage} className="p-3 text-sm">
                    <div className="flex items-baseline justify-between"><span className="font-bold">{s.stage}</span><span className="font-display font-black">{s.sessions}</span></div>
                    <p className="text-[11px] text-slate-400 mt-0.5">Korak: {pct(s.stage_conversion)} · Odustajanje: {s.drop_off ?? "—"} ({pct(s.drop_off_rate)}) · Ukupno: {pct(s.overall_conversion)}</p>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-400">Podaci o ponašanju dostupni od {dt(data.behaviour_available_from)}. Vista je izuzeta (nema kupčevu web sesiju).</p>
            </>
          )}

          {tab === "porudzbine" && data.overview && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Kpi label="Završene" c={{ current: data.overview.completed_orders }} testid="pa-o-completed" />
                <Kpi label="Promet" c={{ current: data.overview.completed_revenue }} fmt={(v) => RSD(v)} testid="pa-o-revenue" />
                <Kpi label="AOV" c={{ current: data.overview.avg_order_value }} fmt={(v) => RSD(v)} testid="pa-o-aov" />
                <Kpi label="Artikala/porudžbini" c={{ current: data.overview.items_per_order }} testid="pa-o-items" />
              </div>
              <button data-testid="pa-export-orders" onClick={() => exportCsv("orders")} className="text-xs font-bold bg-slate-900 text-white rounded-lg px-3 py-1.5">Izvoz CSV</button>
              <Bars rows={data.by_day} labelKey="day" valueKey="orders" testid="pa-o-by-day" />
              <Bars rows={data.by_hour.filter((h) => h.orders)} labelKey="hour" valueKey="orders" testid="pa-o-by-hour" />
              <Table testid="pa-o-by-restaurant" cols={[{ key: "name", label: "Restoran" }, { key: "orders", label: "Porudžbine" },
                { key: "revenue", label: "Promet", fmt: (v) => RSD(v) }]} rows={data.by_restaurant} />
              <Table testid="pa-o-by-city" cols={[{ key: "city_id", label: "Grad" }, { key: "orders", label: "Porudžbine" },
                { key: "revenue", label: "Promet", fmt: (v) => RSD(v) }]} rows={data.by_city} />
            </>
          )}

          {tab === "retencija" && data.order_counts && (
            <>
              <p className="text-[11px] text-slate-400" data-testid="pa-ret-scope">
                Samo završene Ponesi.rs porudžbine (otkazane, odbijene i Vista se ne računaju).
                Kohorte i vrednost kupca gledaju celu istoriju; izabrani datumski opseg važi samo za „Novi vs vraćeni“.
              </p>

              <h3 className="text-[11px] font-black uppercase tracking-wider text-slate-400 pt-1">Broj porudžbina po kupcu</h3>
              <Table testid="pa-ret-counts" cols={[{ key: "bucket", label: "Porudžbina" },
                { key: "customers", label: "Kupaca" }, { key: "share", label: "Udeo", fmt: pct }]}
                rows={data.order_counts.buckets} />
              <p className="text-[11px] text-slate-400">Ukupno kupaca: {data.order_counts.total_customers}</p>

              <h3 className="text-[11px] font-black uppercase tracking-wider text-slate-400 pt-1">Konverzija ponovne kupovine</h3>
              <Table testid="pa-ret-conversions" cols={[{ key: "step", label: "Korak" },
                { key: "customers_at_step", label: "Kupaca" }, { key: "converted", label: "Prešlo dalje" },
                { key: "conversion", label: "Konverzija", fmt: pct }]} rows={data.conversions.steps} />

              <h3 className="text-[11px] font-black uppercase tracking-wider text-slate-400 pt-1">Dani između porudžbina</h3>
              <Table testid="pa-ret-gaps" cols={[{ key: "step", label: "Korak" },
                { key: "avg_days", label: "Prosek (dana)", fmt: (v, r) => (r.small_sample ? "Mali uzorak" : v ?? "—") },
                { key: "median_days", label: "Medijana (dana)", fmt: (v, r) => (r.small_sample ? "—" : v ?? "—") }]}
                rows={[{ step: "Sve", ...data.time_between_orders.overall },
                       ...Object.entries(data.time_between_orders.by_step).map(([k, v]) => ({ step: k, ...v }))]} />

              <h3 className="text-[11px] font-black uppercase tracking-wider text-slate-400 pt-1">Kohortna retencija</h3>
              <div className="bg-white rounded-2xl border overflow-x-auto" data-testid="pa-ret-cohorts">
                <table className="w-full text-sm min-w-[620px]">
                  <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-400">
                    <th className="p-2.5 font-black">Kohorta</th><th className="p-2.5 font-black">Kupaca</th>
                    {data.cohorts.windows.map((w) => <th key={w} className="p-2.5 font-black">{w}</th>)}
                  </tr></thead>
                  <tbody className="divide-y">
                    {data.cohorts.cohorts.length === 0 && (
                      <tr><td className="p-2.5 text-slate-400" colSpan={2 + data.cohorts.windows.length}>Nema podataka.</td></tr>
                    )}
                    {data.cohorts.cohorts.map((c) => (
                      <tr key={c.cohort} data-testid={`pa-ret-cohort-${c.cohort}`}>
                        <td className="p-2.5 font-semibold">{c.cohort}</td>
                        <td className="p-2.5">{c.cohort_size}</td>
                        {c.cells.map((cell) => (
                          <td key={cell.window} className="p-2.5">
                            {cell.status === "pending" ? <span className="text-slate-400 text-[11px]">Podaci u toku</span>
                              : cell.status === "small_sample" ? <span className="text-slate-400 text-[11px]">Mali uzorak</span>
                              : <span className="font-bold">{pct(cell.retention)} <span className="text-slate-400 font-normal text-[11px]">({cell.customers})</span></span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-slate-400">{data.cohorts.note}</p>

              <h3 className="text-[11px] font-black uppercase tracking-wider text-slate-400 pt-1">Novi vs vraćeni (izabrani period)</h3>
              <Table testid="pa-ret-new-returning" cols={[{ key: "kind", label: "Kupci" },
                { key: "customers", label: "Kupaca" }, { key: "completed_orders", label: "Porudžbine" },
                { key: "orders_share", label: "Udeo porudžbina", fmt: pct },
                { key: "revenue", label: "Promet", fmt: (v) => RSD(v) },
                { key: "revenue_share", label: "Udeo prometa", fmt: pct },
                { key: "avg_order_value", label: "AOV", fmt: (v) => RSD(v) }]}
                rows={[{ kind: "Novi", ...data.new_vs_returning.new }, { kind: "Vraćeni", ...data.new_vs_returning.returning }]} />

              <h3 className="text-[11px] font-black uppercase tracking-wider text-slate-400 pt-1">Ostvarena vrednost kupca</h3>
              {data.observed_customer_value.small_sample ? (
                <p className="bg-white rounded-2xl border p-3 text-sm text-slate-400" data-testid="pa-ret-ocv-small">Mali uzorak ({data.observed_customer_value.customers} kupaca) — detalji se ne prikazuju.</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="pa-ret-ocv">
                    <Kpi label="Prosek po kupcu" c={{ current: data.observed_customer_value.avg_value }} fmt={(v) => RSD(v)} testid="pa-ret-ocv-avg" />
                    <Kpi label="Medijana" c={{ current: data.observed_customer_value.median_value }} fmt={(v) => RSD(v)} testid="pa-ret-ocv-median" />
                    <Kpi label="P90" c={{ current: data.observed_customer_value.p90_value }} fmt={(v) => RSD(v)} testid="pa-ret-ocv-p90" />
                    <Kpi label="Top 10% prosek" c={{ current: data.observed_customer_value.top_decile_avg_value }} fmt={(v) => RSD(v)} testid="pa-ret-ocv-top" />
                    <Kpi label="Kupaca u top 10%" c={{ current: data.observed_customer_value.top_decile_customers }} testid="pa-ret-ocv-topn" />
                    <Kpi label="Udeo top 10% u prometu" c={{ current: data.observed_customer_value.top_decile_revenue_share }} fmt={pct} testid="pa-ret-ocv-share" />
                  </div>
                  <p className="text-[11px] text-slate-400">{data.observed_customer_value.note}</p>
                </>
              )}
              <p className="text-[11px] text-slate-400" data-testid="pa-ret-privacy">{data.privacy_note}</p>
            </>
          )}

          {tab === "promocije" && data.totals_by_type && (
            <>
              <p className="text-[11px] text-slate-400" data-testid="pa-promo-note">
                Samo završene Ponesi.rs porudžbine, iz nepromenljivog snapshot-a stavki. Vista je izuzeta.
                Detalji kampanje (doseg, uporedba sa istorijom) vidljivi su u panelu restorana.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[["HAPPY_HOUR", "Happy Hour"], ["RESTAURANT_WIDE_DISCOUNT", "Popust na sve"],
                  ["PRODUCT_DISCOUNT", "Popust na proizvod"]].map(([k, l]) => (
                  <div key={k} className="bg-white rounded-2xl border p-3" data-testid={`pa-promo-total-${k}`}>
                    <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">{l}</p>
                    <p className="font-display font-black text-xl mt-0.5">{RSD(data.totals_by_type[k].completed_revenue)}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      {data.totals_by_type[k].campaigns} kampanja · {data.totals_by_type[k].completed_orders} porudžbina · popust −{RSD(data.totals_by_type[k].discount_given)}
                    </p>
                  </div>
                ))}
              </div>
              <Table testid="pa-promotions" cols={[
                { key: "restaurant_name", label: "Restoran" }, { key: "type_label", label: "Tip" },
                { key: "name", label: "Kampanja" }, { key: "status", label: "Status" },
                { key: "completed_orders", label: "Završene" }, { key: "units_sold", label: "Komada" },
                { key: "base_gross_value", label: "Bruto", fmt: (v) => RSD(v) },
                { key: "discount_given", label: "Popust", fmt: (v) => RSD(v) },
                { key: "completed_revenue", label: "Promet", fmt: (v) => RSD(v) },
                { key: "avg_order_value", label: "AOV", fmt: (v) => RSD(v) }]} rows={data.campaigns} />
            </>
          )}

          {tab === "b2b" && data.funnel && (
            <>
              <p className="text-[11px] text-slate-400" data-testid="pa-b2b-privacy">{data.privacy_note}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                <Kpi label="B2B posetioci" c={{ current: data.kpis.visitors }} testid="pa-b2b-visitors" hint="Jedinstvene sesije sa pregledom /b2b strane." />
                <Kpi label="Početci forme" c={{ current: data.kpis.form_starts }} testid="pa-b2b-starts" />
                <Kpi label="Prijave" c={{ current: data.kpis.applications }} testid="pa-b2b-applications" />
                <Kpi label="Konverzija prijave" c={{ current: data.kpis.application_conversion }} fmt={pct} testid="pa-b2b-app-conv" hint="Prijave / B2B posetioci." />
                <Kpi label="Partneri" c={{ current: data.kpis.partners }} testid="pa-b2b-partners" />
                <Kpi label="Prijava → partner" c={{ current: data.kpis.application_to_partner }} fmt={pct} testid="pa-b2b-partner-conv" />
                <Kpi label="Greške pri slanju" c={{ current: data.kpis.submit_errors }} testid="pa-b2b-errors" />
                <Kpi label="Izbor grada" c={{ current: data.kpis.city_selections }} testid="pa-b2b-city-sel" />
              </div>

              <h3 className="text-[11px] font-black uppercase tracking-wider text-slate-400 pt-1">Funnel akvizicije</h3>
              <div className="bg-white rounded-2xl border divide-y" data-testid="pa-b2b-funnel">
                {data.funnel.stages.map((s) => (
                  <div key={s.stage} className="p-3 text-sm">
                    <div className="flex items-baseline justify-between"><span className="font-bold">{s.label}</span><span className="font-display font-black">{s.count}</span></div>
                    <p className="text-[11px] text-slate-400 mt-0.5">Korak: {pct(s.stage_conversion)} · Odustajanje: {s.drop_off ?? "—"} · Ukupno: {pct(s.overall_conversion)}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="pa-b2b-conversions">
                <Kpi label="Prijava → kontakt" c={{ current: data.funnel.conversions.application_to_contacted }} fmt={pct} testid="pa-b2b-c1" />
                <Kpi label="Kontakt → razgovor" c={{ current: data.funnel.conversions.contacted_to_in_discussion }} fmt={pct} testid="pa-b2b-c2" />
                <Kpi label="Razgovor → partner" c={{ current: data.funnel.conversions.in_discussion_to_partner }} fmt={pct} testid="pa-b2b-c3" />
                <Kpi label="Prijava → partner" c={{ current: data.funnel.conversions.application_to_partner }} fmt={pct} testid="pa-b2b-c4" />
              </div>
              <p className="text-[11px] text-slate-400">{data.funnel.note}</p>
              {data.cohort_maturity.maturing && (
                <p className="text-[11px] text-brand-orange font-bold" data-testid="pa-b2b-maturing">{data.cohort_maturity.note}</p>
              )}

              <h3 className="text-[11px] font-black uppercase tracking-wider text-slate-400 pt-1">Trenutni pipeline</h3>
              <Table testid="pa-b2b-pipeline" cols={[{ key: "status", label: "Status" }, { key: "leads", label: "Lead-ova" }]}
                rows={Object.entries(data.current_pipeline).map(([k, v]) => ({ status: k, leads: v }))} />

              <h3 className="text-[11px] font-black uppercase tracking-wider text-slate-400 pt-1">Vreme do partnerstva</h3>
              <Table testid="pa-b2b-timing" cols={[{ key: "step", label: "Korak" },
                { key: "count", label: "Lead-ova" },
                { key: "median_days", label: "Medijana (dana)", fmt: (v, r) => (r.status === "READY" ? v : "Nema podataka") },
                { key: "avg_days", label: "Prosek (dana)", fmt: (v, r) => (r.status === "READY" ? v : "—") },
                { key: "p90_days", label: "P90", fmt: (v, r) => (r.status === "READY" ? v : "—") }]}
                rows={[["Prijava → kontakt", "application_to_contacted"], ["Kontakt → razgovor", "contacted_to_in_discussion"],
                       ["Razgovor → partner", "in_discussion_to_partner"], ["Prijava → partner", "application_to_partner"]]
                  .map(([label, key]) => ({ step: label, ...data.timing[key] }))} />

              <h3 className="text-[11px] font-black uppercase tracking-wider text-slate-400 pt-1">Zastoj u pipeline-u</h3>
              <Table testid="pa-b2b-aging" cols={[{ key: "status", label: "Trenutni status" },
                { key: "count", label: "Lead-ova" },
                { key: "median_days", label: "Medijana dana u statusu", fmt: (v, r) => (r.status_state === "READY" ? v : "—") },
                { key: "avg_days", label: "Prosek dana", fmt: (v, r) => (r.status_state === "READY" ? v : "—") }]}
                rows={["NEW", "CONTACTED", "IN_DISCUSSION"].map((k) => ({ status: k, ...data.aging[k], status_state: data.aging[k].status }))} />
              <p className="text-[11px] text-slate-400" data-testid="pa-b2b-uncontacted">
                Nekontaktiranih (NEW): {data.uncontacted.new_leads} · preko 1 dana: {data.uncontacted.over_1_days} · 3 dana: {data.uncontacted.over_3_days} · 7 dana: {data.uncontacted.over_7_days}. {data.uncontacted.note}
              </p>

              <h3 className="text-[11px] font-black uppercase tracking-wider text-slate-400 pt-1">Odbijeni lead-ovi</h3>
              <p className="text-[11px] text-slate-400">Odbijeno: {data.rejections.rejected} ({pct(data.rejections.rejection_rate)}). {data.rejections.note}</p>
              <Table testid="pa-b2b-rejections" cols={[{ key: "reason", label: "Razlog" }, { key: "leads", label: "Lead-ova" }]} rows={data.rejections.by_reason} />

              {[["Po gradu", "by_city", "pa-b2b-by-city"], ["Po izvoru saobraćaja", "by_source", "pa-b2b-by-source"],
                ["Po UTM kampanji", "by_campaign", "pa-b2b-by-campaign"]].map(([title, key, tid]) => (
                <div key={tid}>
                  <h3 className="text-[11px] font-black uppercase tracking-wider text-slate-400 pt-1">{title}</h3>
                  <Table testid={tid} cols={[{ key: "key", label: title.replace("Po ", "") },
                    { key: "applications", label: "Prijave" }, { key: "contacted", label: "Kontaktirano" },
                    { key: "in_discussion", label: "U razgovoru" }, { key: "partners", label: "Partneri" },
                    { key: "application_to_partner", label: "Prijava → partner", fmt: pct },
                    { key: "median_days_to_partner", label: "Medijana dana", fmt: (v) => v ?? "—" }]}
                    rows={data[key]} />
                </div>
              ))}

              <a href="/admin/b2b" data-testid="pa-b2b-open-leads" className="inline-block text-xs font-bold bg-slate-900 text-white rounded-lg px-3 py-1.5">Otvori B2B lead-ove</a>
              <p className="text-[11px] text-slate-400">Podaci o ponašanju dostupni od {dt(data.behaviour_available_from)}. Prijave i statusi postoje i istorijski.</p>
            </>
          )}

          {tab === "tehnicki" && data.health && (
            <TechAnalytics d={data} mode={techMode} onMode={(m) => setF({ tw: m === "range" ? "" : m })} />
          )}

          {tab === "pretraga" && data.queries && (
            <>
              <p className="text-[11px] text-slate-400">Upiti su vidljivi samo platformi. Prikazuju se od {data.min_occurrences} pojave.</p>
              <Table testid="pa-searches" cols={[{ key: "normalized_query", label: "Upit" }, { key: "searches", label: "Pretraga" },
                { key: "sessions", label: "Sesija" }, { key: "no_results", label: "Bez rezultata" },
                { key: "avg_results", label: "Prosečno rezultata" }]} rows={data.queries} />
            </>
          )}

          {tab === "kampanje" && data.campaigns && (
            <>
              <button data-testid="pa-export-campaigns" onClick={() => exportCsv("campaigns")} className="text-xs font-bold bg-slate-900 text-white rounded-lg px-3 py-1.5">Izvoz CSV</button>
              <Table testid="pa-campaigns" cols={[{ key: "campaign", label: "Kampanja" }, { key: "source", label: "Izvor" },
                { key: "visitors", label: "Posetioci" }, { key: "registrations", label: "Registracije" },
                { key: "orders", label: "Porudžbine" }, { key: "completed_orders", label: "Završene" },
                { key: "revenue", label: "Promet", fmt: (v) => RSD(v) },
                { key: "conversion", label: "Konverzija", fmt: pct }]} rows={data.campaigns} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
